<template>
	<div class="container-fluid py-4">
		<div class="row mb-4">
			<div class="col">
				<h1 class="h2">Treatments</h1>
				<p class="text-muted">
					Review treatments recorded across your organization
				</p>
			</div>
		</div>

		<div class="row">
			<div class="col">
				<div class="card">
					<div class="card-body">
						<div v-if="loading" class="text-center py-5 text-muted">
							<p>Loading treatments…</p>
						</div>

						<div v-else class="table-responsive">
							<table class="table table-hover">
								<thead>
									<tr>
										<th>Treatment ID</th>
										<th>Area (m²)</th>
										<th>Quantity</th>
										<th>Quantity Unit</th>
										<th>Start Time</th>
										<th>Duration</th>
										<th>Method</th>
									</tr>
								</thead>
								<tbody>
									<tr
										v-for="treatment in treatments"
										:key="treatment.id"
										style="cursor: pointer"
										@click="openDetail(treatment.id)"
									>
										<td>
											<RouterLink :to="detailRoute(treatment.id)" @click.stop>
												{{ treatment.id }}
											</RouterLink>
										</td>
										<td>{{ formatArea(treatment.area_treated) }}</td>
										<td>{{ formatQuantity(treatment.quantity) }}</td>
										<td>{{ treatment.quantity_unit || "—" }}</td>
										<td>
											<TimeRelativeWithTooltip
												:time="treatment.start_datetime"
											/>
										</td>
										<td>
											{{
												formatDuration(
													durationSeconds(
														treatment.start_datetime,
														treatment.end_datetime,
													),
												)
											}}
										</td>
										<td>{{ treatment.method || "—" }}</td>
									</tr>
								</tbody>
							</table>

							<div
								v-if="treatments.length === 0"
								class="text-center py-5 text-muted"
							>
								<p>No treatments found</p>
							</div>
						</div>
					</div>

					<div
						v-if="!loading && treatments.length > 0"
						class="card-footer d-flex justify-content-between align-items-center"
					>
						<button
							class="btn btn-outline-secondary btn-sm"
							:disabled="offset <= 0"
							@click="goPrev"
						>
							<i class="bi bi-chevron-left"></i> Prev
						</button>
						<span class="text-muted small">
							{{ offset + 1 }} – {{ offset + treatments.length }} of {{ total }}
						</span>
						<button
							class="btn btn-outline-secondary btn-sm"
							:disabled="offset + treatments.length >= total"
							@click="goNext"
						>
							Next <i class="bi bi-chevron-right"></i>
						</button>
					</div>
				</div>
			</div>
		</div>
	</div>

	<ToastNotification
		:show="toastShow"
		:title="toastTitle"
		:message="toastMessage"
	/>
</template>

<script setup lang="ts">
import { onMounted, ref, watch } from "vue";
import { useRouter } from "vue-router";

import TimeRelativeWithTooltip from "@/components/TimeRelativeWithTooltip.vue";
import ToastNotification from "@/components/ToastNotification.vue";
import { durationSeconds, formatDuration } from "@/format";
import { useStoreTreatment } from "@/store/treatment";
import type { MapReviewTreatmentRow } from "@/type/api";

const formatArea = (area: number | null): string => {
	return area === null ? "—" : area.toLocaleString();
};

const formatQuantity = (quantity: number | null): string => {
	return quantity === null ? "—" : quantity.toLocaleString();
};

const store = useStoreTreatment();
const router = useRouter();

const treatments = ref<MapReviewTreatmentRow[]>([]);
const loading = ref(false);
const limit = ref(50);
const offset = ref(0);
const total = ref(0);
const toastShow = ref(false);
const toastTitle = ref("");
const toastMessage = ref("");

const showToast = (title: string, message: string): void => {
	toastTitle.value = title;
	toastMessage.value = message;
	toastShow.value = true;
};

const detailRoute = (id: number): { name: string; params: { id: string } } => {
	return { name: "Treatment Review Detail", params: { id: id.toString() } };
};

const openDetail = (id: number): void => {
	router.push(detailRoute(id));
};

const goPrev = (): void => {
	offset.value = Math.max(0, offset.value - limit.value);
};

const goNext = (): void => {
	offset.value += limit.value;
};

const load = async (): Promise<void> => {
	loading.value = true;
	try {
		// The page's own total is the envelope's `filtered_total`: the treatments
		// the district holds, which is also what the pager counts against.
		const page = await store.list(limit.value, offset.value);
		treatments.value = page.items;
		total.value = page.metadata.filtered_total;
	} catch (error) {
		const message = error instanceof Error ? error.message : String(error);
		showToast("Error", `Failed to load treatments: ${message}`);
	} finally {
		loading.value = false;
	}
};

// Refetch when pagination moves.
watch(offset, () => {
	void load();
});

onMounted(load);
</script>

<style scoped>
.table th {
	font-weight: 600;
	border-bottom: 2px solid #dee2e6;
}
</style>
